import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormikErrors, setNestedObjectValues } from "formik";
import { CmpProfileResultPopup } from "./CmpProfile/CmpProfileLayout";
import { CmpProfileIssue } from "./CmpProfile/cmpProfileSections";
import { CmpSuccessDialog } from "./CmpProfile/CmpDesignDialog";

// CMP Create/Update Perk save flow (CMP Accelerator Member Dashboard (2)):
// - MD-K1/K2/K3 (ACC:1189-1196, 3164, 3174): the success pop-up stays until closed, then the perks list opens.
// - MD-K13 (ACC:1857-1885): Save & Update in UNSAVED CHANGES saves and goes to the clicked page;
//   missing fields show the MISSING REQUIRED FIELDS list, with Leave Without Saving.

const PERK_FIELD_LABELS: Record<string, string> = {
    title: "Perk Title",
    description: "Perk Description",
    isReoccuring: "Redemption Frequency",
    startDate: "Start Date",
    endDate: "End Date",
    "redeemSettings.whereToRedeem": "Where to Redeem",
    "redeemSettings.howToRedeem.code": "Promo Code",
    "redeemSettings.howToRedeem.websiteToEnterCodeAt": "Website URL",
    "redeemSettings.howToRedeem.contactName": "Contact Name",
    "redeemSettings.howToRedeem.contactEmail": "Contact Email",
    "redeemSettings.howToRedeem.contactNumber": "Contact Number",
};

const flatten = (errors: unknown, prefix = ""): [string, string][] => {
    if (typeof errors === "string") return [[prefix, errors]];
    if (!errors || typeof errors !== "object") return [];
    return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) => flatten(value, prefix ? `${prefix}.${key}` : key));
};

/** Formik errors as the design MISSING REQUIRED FIELDS rows, in form order. */
export const perkErrorsToIssues = (errors: FormikErrors<unknown>): CmpProfileIssue[] => {
    const order = Object.keys(PERK_FIELD_LABELS);
    return flatten(errors)
        .sort(([a], [b]) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)))
        .map(([path, why]) => ({
            field: PERK_FIELD_LABELS[path] || path,
            kind: /valid/i.test(why) ? "invalid" as const : "missing" as const,
            why,
            paths: [path],
        }));
};

type PerkFormik = {
    validateForm: () => Promise<FormikErrors<any>>;
    setTouched: (touched: any, shouldValidate?: boolean) => unknown;
    submitForm: () => Promise<unknown>;
};

export const useCmpPerkSaveFlow = ({ listPath, successMessage }: { listPath: string; successMessage: string }) => {
    const history = useHistory();
    const leaveTo = useRef<string | null>(null);
    const [leaveWithoutSaving, setLeaveWithoutSaving] = useState<{ run: () => void } | null>(null);
    const [issues, setIssues] = useState<CmpProfileIssue[] | null>(null);
    const [successOpen, setSuccessOpen] = useState(false);

    /** UnsavedChangesPrompt onSaveAndUpdate: the page navigates itself once the perk is saved. */
    const saveAndLeave = async (formik: PerkFormik, to: string) => {
        const errors = await formik.validateForm();
        const rows = perkErrorsToIssues(errors);
        if (rows.length) {
            formik.setTouched(setNestedObjectValues(errors, true), false);
            setIssues(rows);
            return false;
        }
        leaveTo.current = to;
        await formik.submitForm();
        return false;
    };

    /** Call once the perk is saved and the unsaved-changes prompt is gone. */
    const afterSaved = () => {
        const to = leaveTo.current;
        leaveTo.current = null;
        if (to) history.push(to);
        else setSuccessOpen(true);
    };

    const dialogs = (
        <>
            {issues && (
                <CmpProfileResultPopup result={{ ok: false, issues }} onClose={() => { setIssues(null); setLeaveWithoutSaving(null); }}
                    onLeaveWithoutSaving={leaveWithoutSaving ? () => {
                        setIssues(null);
                        setLeaveWithoutSaving(null);
                        leaveWithoutSaving.run();
                    } : undefined} />
            )}
            <CmpSuccessDialog open={successOpen} testId="cmp-perk-success" message={successMessage}
                onClose={() => { setSuccessOpen(false); history.push(listPath); }} />
        </>
    );

    return {
        saveAndLeave,
        onSaveFailed: (leave: () => void) => setLeaveWithoutSaving({ run: leave }),
        afterSaved,
        dialogs,
    };
};
